import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    IconButton,
    Stack
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import EventsApi from '../../../../api/eventsApi/eventsApi';
import { PDFViewer, PDFDownloadLink } from '@react-pdf/renderer';
import ReportDocument from './ReportDocument';
import { blobToBase64 } from './reportUtils';
import { ReportData, FileAttachment } from './reportTypes';

interface ReportTemplateModalProps {
    open: boolean;
    onClose: () => void;
    reportData?: ReportData;
    extraAttachments?: { [key: string]: string };
}

const ReportTemplate: React.FC<ReportTemplateModalProps> = ({
    open,
    onClose,
    reportData,
    extraAttachments
}): JSX.Element | null => {
    const [loading, setLoading] = useState(false);
    const [attachments, setAttachments] = useState<{ [key: string]: string }>({});
    const [userData, setUserData] = useState<{ firstName: string; lastName: string } | null>(null);

    useEffect(() => {
        const fetchUserData = async () => {
            try {
                const userInfo = await EventsApi.getUser();
                setUserData({ firstName: userInfo.firstName, lastName: userInfo.lastName });
            } catch (error) {
                console.error('Error fetching user data:', error);
            }
        };
        fetchUserData();
    }, []);

    useEffect(() => {
        const loadAttachments = async () => {
            if (!reportData) return;
            const newAttachments: { [key: string]: string } = {};

            // Görüntü dosyası uzantıları
            const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg'];
            
            // Pin ve soru cevaplarındaki sadece görüntü dosyalarını topla
            const allFiles = [
                ...(reportData.pinInfo.fileAttachments || []),
                ...reportData.questionAnswers.flatMap(qa => qa.answer.fileAttachments || [])
            ].filter(file => {
                const fileName = file.fileName?.toLowerCase() || '';
                return imageExtensions.some(ext => fileName.endsWith(ext));
            });

            // Sadece görüntü dosyalarını Base64'e dönüştürüp state'e kaydet
            for (const file of allFiles) {
                try {
                    const blob = await EventsApi.getFileAttachment(file.id);
                    const base64data = await blobToBase64(blob);
                    newAttachments[file.id] = base64data;
                } catch (error) {
                    // Sessizce devam et - dosya bulunamadıysa veya hata varsa atla
                    console.warn(`Attachment ${file.id} (${file.fileName}) could not be loaded:`, error);
                }
            }

            // ReportLocationPng varsa, onu da attachments'a ekle
            if (reportData.pinInfo.reportLocationPng) {
                try {
                    const blob = await EventsApi.getFileAttachment(reportData.pinInfo.reportLocationPng);
                    const base64data = await blobToBase64(blob);
                    newAttachments[reportData.pinInfo.reportLocationPng] = base64data;
                } catch (error) {
                    console.warn(`ReportLocationPng (${reportData.pinInfo.reportLocationPng}) could not be loaded:`, error);
                }
            }

            // Merge runtime-generated images (charts etc.) into the same attachment map.
            setAttachments({ ...newAttachments, ...(extraAttachments || {}) });
        };
        loadAttachments();
    }, [reportData, extraAttachments]);

    const handlePdfSave = async (blob: Blob) => {
        if (!reportData?.pinInfo?.Id) return;
        try {
            setLoading(true);
            const formData = new FormData();
            const pdfFile = new File([blob], `${reportData.pinInfo.Name}_rapor.pdf`, { type: 'application/pdf' });
            formData.append('Id', reportData.pinInfo.Id);
            formData.append('Files', pdfFile);
            await EventsApi.updatePin(reportData.pinInfo.Id, formData);
            console.log('PDF saved successfully');
        } catch (error) {
            console.error('Error saving PDF:', error);
        } finally {
            setLoading(false);
        }
    };

    if (!reportData) return null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{ sx: { maxHeight: '95vh', bgcolor: '#f4f7fc', m: 2 } }}
        >
            <DialogContent sx={{ p: 0, '&::-webkit-scrollbar': { width: '12px' } }}>
                <Stack direction="row" spacing={2} sx={{ position: 'absolute', right: 85, top: 12, zIndex: 1 }}>
                    <PDFDownloadLink
                        document={<ReportDocument reportData={reportData} attachments={attachments} userData={userData} />}
                        fileName={`${reportData.pinInfo.Name}_rapor.pdf`}
                    >
                        {({ blob, loading }) => (
                            <IconButton
                                onClick={() => blob && handlePdfSave(blob)}
                                disabled={loading || !blob}
                                sx={{ bgcolor: 'rgba(255,255,255,0.9)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' }, '&:disabled': { opacity: 0.5 } }}
                                title="Raporu Kaydet"
                            >
                                <SaveIcon />
                            </IconButton>
                        )}
                    </PDFDownloadLink>
                    <IconButton
                        onClick={onClose}
                        sx={{ color: '#2c3e50', bgcolor: 'rgba(255,255,255,0.9)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }}
                        title="Kapat"
                    >
                        <CloseIcon />
                    </IconButton>
                </Stack>
                <PDFViewer style={{ width: '100%', height: '90vh' }}>
                    <ReportDocument reportData={reportData} attachments={attachments} userData={userData} />
                </PDFViewer>
            </DialogContent>
        </Dialog>
    );
};

export default ReportTemplate;